import type {
  ChatPlayerAuthoredMessage,
  ChatPlayerAuthoredPart,
  ChatPlayerMessage,
  ChatPlayerPartTiming,
  ChatPlayerScenarioSource,
  ChatPlayerTypingTiming,
} from "./chat-player-types";
import { normalizeChatPlayerCompiledChat } from "./chat-player-normalize";
import {
  clampMs,
  estimateSpeechDurationMs,
  makeStableId,
  normalizeTimedWordSpacing,
  synthesizeTimedWords,
} from "./chat-player-timing";

const MESSAGE_GAP_MS = 420;
const PART_GAP_MS = 150;
const TOOL_DEFAULT_DURATION_MS = 1200;
const CUSTOM_DEFAULT_DURATION_MS = 700;

export function compileChatPlayerScenario(source: ChatPlayerScenarioSource) {
  const messages: ChatPlayerMessage[] = [];
  const partTimings: ChatPlayerPartTiming[] = [];
  const typingTimings: ChatPlayerTypingTiming[] = [];
  let cursorMs = source.initialMessages?.length ? MESSAGE_GAP_MS : 0;
  let durationMs = 0;

  source.initialMessages?.forEach((message, index) => {
    messages.push(
      compileScenarioMessage(message, {
        fallbackId: `initial-${index}`,
        immediate: true,
        partTimings,
        startMs: 0,
      }).message,
    );
  });

  source.messages.forEach((message, index) => {
    const loadingStartMs = cursorMs + getAuthoredMs(message.delayBeforeMs);
    const loadingEndMs = loadingStartMs + getAuthoredMs(message.loadingBeforeMs);
    const compiled = compileScenarioMessage(message, {
      fallbackId: `message-${index}`,
      immediate: false,
      partTimings,
      startMs: loadingEndMs,
    });
    const holdEndMs = compiled.endMs + getAuthoredMs(message.visibleDurationMs);

    typingTimings.push({
      messageId: compiled.message.id,
      startMs: loadingStartMs,
      endMs: loadingEndMs,
    });
    messages.push(compiled.message);
    durationMs = Math.max(durationMs, holdEndMs);
    cursorMs = Math.max(cursorMs, holdEndMs + MESSAGE_GAP_MS);
  });

  return normalizeChatPlayerCompiledChat({
    id: source.id ?? "scenario-chat",
    messages,
    partTimings,
    typingTimings,
    durationMs,
  });
}

function compileScenarioMessage(
  message: ChatPlayerAuthoredMessage,
  {
    fallbackId,
    immediate,
    partTimings,
    startMs,
  }: {
    fallbackId: string;
    immediate: boolean;
    partTimings: ChatPlayerPartTiming[];
    startMs: number;
  },
) {
  const messageId = message.id ?? fallbackId;
  const parts: ChatPlayerMessage["parts"] = [];
  let cursorMs = clampMs(startMs);

  message.parts.forEach((part, index) => {
    const partId = part.id ?? makeStableId([messageId, part.type, index]);
    const partStartMs = immediate ? 0 : cursorMs + (index === 0 ? 0 : PART_GAP_MS);
    const compiled = compileScenarioPart(part, {
      immediate,
      partId,
      startMs: partStartMs,
    });

    parts.push(compiled.part);
    partTimings.push({
      messageId,
      partId: compiled.part.id,
      startMs: compiled.startMs,
      endMs: compiled.endMs,
    });

    cursorMs = immediate ? 0 : compiled.endMs;
  });

  return {
    message: {
      id: messageId,
      role: message.role,
      parts,
    },
    endMs: cursorMs,
  };
}

function compileScenarioPart(
  part: ChatPlayerAuthoredPart,
  {
    immediate,
    partId,
    startMs,
  }: {
    immediate: boolean;
    partId: string;
    startMs: number;
  },
) {
  const normalizedStartMs = clampMs(startMs);

  switch (part.type) {
    case "speech": {
      const durationMs = part.durationMs ?? estimateSpeechDurationMs(part.content);
      const endMs = getScenarioPartEndMs(normalizedStartMs, durationMs, immediate);
      const words = normalizeTimedWordSpacing(
        synthesizeTimedWords({
          text: part.content,
          idPrefix: partId,
          startMs: normalizedStartMs,
          endMs,
          immediate,
        }),
      );
      const compiledPart = { id: partId, type: "speech", words } satisfies ChatPlayerMessage["parts"][number];

      return {
        part: compiledPart,
        startMs: normalizedStartMs,
        endMs,
      };
    }

    case "text": {
      const durationMs = part.durationMs ?? 0;
      const compiledPart = {
        id: partId,
        type: "text",
        content: part.content,
      } satisfies ChatPlayerMessage["parts"][number];

      return {
        part: compiledPart,
        startMs: normalizedStartMs,
        endMs: getScenarioPartEndMs(normalizedStartMs, durationMs, immediate),
      };
    }

    case "tool": {
      const durationMs = part.durationMs ?? TOOL_DEFAULT_DURATION_MS;
      const compiledPart = {
        id: partId,
        type: "tool",
        name: part.name,
        data: part.data,
      } satisfies ChatPlayerMessage["parts"][number];

      return {
        part: compiledPart,
        startMs: normalizedStartMs,
        endMs: getScenarioPartEndMs(normalizedStartMs, durationMs, immediate),
      };
    }

    case "custom": {
      const durationMs = part.durationMs ?? CUSTOM_DEFAULT_DURATION_MS;
      const compiledPart = {
        id: partId,
        type: "custom",
        renderKey: part.renderKey,
        data: part.data,
      } satisfies ChatPlayerMessage["parts"][number];

      return {
        part: compiledPart,
        startMs: normalizedStartMs,
        endMs: getScenarioPartEndMs(normalizedStartMs, durationMs, immediate),
      };
    }
  }
}

function getScenarioPartEndMs(startMs: number, durationMs: number, immediate: boolean) {
  return immediate ? startMs : startMs + durationMs;
}

function getAuthoredMs(value: number | undefined) {
  return typeof value === "number" && Number.isFinite(value) ? clampMs(value) : 0;
}
